import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Row, Col, Grid, Skeleton, notification, Tabs, Card, Descriptions, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { WhiteOrBlackList } from '../../../components';
import getRandomString from '../../../helpers/getRandomString';
import getOr from '../../../helpers/getOr';
import boolToSmallInt from '../../../helpers/boolToSmallInt';
import { createApiGateway } from '../../../apiGateway';
import validations from '../ssps/utils/validations';
import alpha3CountryCodeList from '../../../constants/alpha3CountryCodeList.json';
import { list } from '../../../constants/endpoints';
import { authorizedDELETE } from '../../../helpers/requester';
import SellerInfo from '../ssps/SellerInfo';
import UpsertSspForm from '../../../sections/ssps/UpsertSspForm';
import { UpsertSspFormBidRequestSwapper } from '../../../sections/ssps/UpsertSspFormBidRequestSwapper';
import { UpsertSspFormOverpay } from '../../../sections/ssps/UpsertSspFormOverpay';
import { WhiteAndBlackListName, WhiteAndBlackListType } from '../../../apiGateway/whiteAndBlackLists';
import { validateSspBeforeUpsert } from './utils';
import { UpsertSspFormUserSync } from '../../../sections/ssps/UpsertSspFormUserSync';
import { useDisabledModules } from '../../../hooks/useDisabledModules';

export default function SspPage() {
  const params = useParams();

  const sspId = params.id;
  const isCreateMode = sspId === 'new';

  // @ts-expect-error fix this
  const userInfo = useSelector((state) => state.login);

  const apiGateway = createApiGateway();

  const screens = Grid.useBreakpoint();
  const [existingSsp, setExistingSsp] = useState();
  const [loading, setLoading] = useState(true);
  const [dsps, setDsps] = useState([]);
  const [ssps, setSsps] = useState([]);
  const [sspDspWhiteList, setSspDspWhiteList] = useState([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [iabCategories, setIabCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [whiteLists, setWhiteLists] = useState({});

  const company = existingSsp ? users.find((user) => user.id === existingSsp.partnerId) : null;
  const disabledModules = useDisabledModules();

  const onUpsertSsp = () => {
    setTimeout(() => {
      window.location = '/ssps' + (existingSsp ? `/${existingSsp.id}` : '');
    }, 1000 * 2);
  };

  useEffect(() => {
    (async () => {
      const usersResponse = await apiGateway.users.getAll();
      setUsers(usersResponse.filter((x) => x.role === 'user'));

      if (isCreateMode) {
        setLoading(false);

        return;
      }

      const ssp = await apiGateway.ssps.getById(sspId);

      // eslint-disable-next-line @typescript-eslint/no-shadow
      const [dsps, ssps, sspDspWhiteList, countriesWhiteList] = await Promise.all([
        apiGateway.dsps.getAll({ includeSpend: false }),
        apiGateway.ssps.getAll({ includeSpend: false }),
        apiGateway.whiteAndBlackLists.list({
          ssp_id: sspId,
          name: WhiteAndBlackListName.whiteListDsp,
          type: WhiteAndBlackListType.White,
        }),
        apiGateway.whiteAndBlackLists.list({
          ssp_id: sspId,
          name: WhiteAndBlackListName.whiteListSspCountries,
          type: WhiteAndBlackListType.White,
        }),
      ]);

      if (_.isString(ssp.catsForHB) && _.size(ssp.catsForHB)) {
        setIabCategories(ssp.catsForHB.split(','));
      } else {
        setIabCategories([]);
      }

      setWhiteLists({
        ...whiteLists,
        countriesWhiteList,
      });

      setExistingSsp(ssp);
      setDsps(dsps);
      setSsps(ssps);
      setSspDspWhiteList(sspDspWhiteList);

      setLoading(false);
    })();
  }, []);

  const updateSsp = async (formObj) => {
    try {
      const ssp = boolToSmallInt({
        ...formObj,
        authName: existingSsp.authName,
        authPassword: existingSsp.authPassword,
      });

      validations.validateSsp(ssp);

      await apiGateway.ssps.updateSsp(existingSsp.id, ssp);

      notification.success({ title: 'SSP updated' });

      onUpsertSsp();
    } catch (e) {
      notification.error({ title: `Error when updating an SSP: ${e}` });
    }
  };

  const createSsp = async (formObj) => {
    const ssp = boolToSmallInt({
      ...formObj,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      authName: getAuthName(getOr(formObj, 'name', '')),
      authPassword: getRandomString({ useMd5: true }),
    });

    try {
      validations.validateSsp(ssp);

      await apiGateway.ssps.createSsp(ssp);

      notification.success({ title: 'SSP created' });

      onUpsertSsp();
    } catch (e) {
      notification.error({ title: `Error when creating an SSP: ${e}` });
    }
  };

  const onFormFinish = (values) => {
    const dataToSave = _.omitBy(
      {
        ...(existingSsp || {}),
        ...values,
        catsForHB: values.iabCategories ? values.iabCategories.join(',') : null,
      },
      _.isNil
    );

    try {
      validateSspBeforeUpsert(dataToSave);
    } catch (e) {
      notification.error({ title: e.message });

      return;
    }

    return (isCreateMode ? createSsp : updateSsp)(dataToSave);
  };

  const getAuthName = (name) =>
    name
      .toLowerCase()
      .split('')
      .filter((c) => /\w/.test(c))
      .join('');

  const onEnd = () => {};

  // TODO: Move to apiGateway
  const onDelete = (baseUrl) => async (e, data) => {
    try {
      await authorizedDELETE(baseUrl.replace(':id', data.id));
      notification.success({ title: 'Deleted' });
    } catch (error) {
      notification.error({ title: `Oops.. sth went wrong: ${error}` });
    }
  };

  const allowedWhiteListDspValues = dsps
    .map((x) => ({ dsp_id: x.id, name: x.name, active: x.isActive ? '+' : '-' }))
    .map((x) => {
      const sameFromApi = sspDspWhiteList.find((y) => y.dsp_id === x.dsp_id);

      return {
        __toggle: Boolean(sameFromApi),
        ...x,
        id: (sameFromApi && sameFromApi.id) || null,
      };
    });

  const allowedWhiteListCountryValues = alpha3CountryCodeList
    .map((x) => ({
      value: x.alpha3,
      name: x.country,
    }))
    .map((x) => {
      const sameFromApi = (whiteLists.countriesWhiteList || []).find((y) => y.value === x.value);

      return {
        __toggle: Boolean(sameFromApi),
        ...x,
        id: (sameFromApi && sameFromApi.id) || null,
      };
    });

  if (loading) {
    return (
      <Row>
        <Col xs={24} md={{ span: 20, offset: 2 }}>
          <Skeleton active />
        </Col>
      </Row>
    );
  }

  return (
    <Row>
      <Col xs={24} md={{ span: 20, offset: 2 }}>
        {existingSsp && (
          <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
            <Descriptions
              size="small"
              column={1}
              layout={screens.md ? 'horizontal' : 'vertical'}
              styles={{ label: { width: screens.md ? 140 : 'auto' } }}
            >
              {company && (
                <Descriptions.Item label="Company">
                  [{company.id}] {company.firstName}
                </Descriptions.Item>
              )}

              <Descriptions.Item label="SSP">
                [{existingSsp.id}] {existingSsp.name || '-'}
              </Descriptions.Item>

              {existingSsp.statUrl && (
                <Descriptions.Item label="Stats URL">
                  <Typography.Paragraph
                    style={{ marginBottom: 0 }}
                    copyable={{ text: existingSsp.statUrl }}
                    ellipsis={{ rows: 1, tooltip: existingSsp.statUrl }}
                  >
                    <a href={existingSsp.statUrl} target="_blank" rel="noreferrer">
                      {existingSsp.statUrl}
                    </a>
                  </Typography.Paragraph>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Endpoints (US East)">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div>
                    <a
                      href={`http://useast.justbidit.xyz:8800/?name=${existingSsp.authName}&pass=${existingSsp.authPassword}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      http://useast.justbidit.xyz:8800/?name={existingSsp.authName}&pass=••••
                    </a>
                  </div>
                  <div>
                    <a
                      href={`https://useast.creativedot2.net:8844/?name=${existingSsp.authName}&pass=${existingSsp.authPassword}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      https://useast.creativedot2.net:8844/?name={existingSsp.authName}&pass=••••
                    </a>
                  </div>
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Endpoints (US West)">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div>
                    <a
                      href={`http://uswest.justbidit.xyz:8800/?name=${existingSsp.authName}&pass=${existingSsp.authPassword}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      http://uswest.justbidit.xyz:8800/?name={existingSsp.authName}&pass=••••
                    </a>
                  </div>
                  <div>
                    <a
                      href={`https://uswest.creativedot2.net:8844/?name=${existingSsp.authName}&pass=${existingSsp.authPassword}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      https://uswest.creativedot2.net:8844/?name={existingSsp.authName}&pass=••••
                    </a>
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        <Tabs
          defaultActiveKey="general"
          destroyOnHidden={false}
          items={[
            {
              key: 'general',
              label: 'General',
              children: <UpsertSspForm ssp={existingSsp} onFinish={onFormFinish} users={users} />,
            },
            ...(existingSsp
              ? [
                  {
                    key: 'userSync',
                    label: 'User Sync',
                    children: (
                      <UpsertSspFormUserSync
                        sspId={existingSsp.userSyncSspId || existingSsp.id}
                        ssps={ssps}
                        onFinish={onFormFinish}
                        currentSspId={existingSsp.id}
                      />
                    ),
                  },
                ]
              : []),
            ...(disabledModules.includes('swapper')
              ? []
              : [
                  {
                    key: 'swapper',
                    label: 'Bid Request Swapper',
                    children: <UpsertSspFormBidRequestSwapper ssp={existingSsp} ssps={ssps} onFinish={onFormFinish} />,
                  },
                ]),
            ...(userInfo && ['admin', 'localAdmin'].includes(userInfo.role) && !disabledModules.includes('overpay')
              ? [
                  {
                    key: 'overpay',
                    label: 'Overpay',
                    children: <UpsertSspFormOverpay ssp={existingSsp} onFinish={onFormFinish} />,
                  },
                ]
              : []),
            ...(existingSsp
              ? [
                  {
                    key: 'whiteLists',
                    label: 'White Lists',
                    children: (
                      <>
                        <WhiteOrBlackList
                          title="Add DSP to WL"
                          primaryKeyName="ssp_id"
                          keyName="dsp_id"
                          parseValFunc={Number}
                          postUrl={list.v2}
                          onEnd={onEnd}
                          onDelete={onDelete(list.v2Delete)}
                          allowedValues={allowedWhiteListDspValues}
                          hiddenColumns={['id']}
                          id={existingSsp.id}
                          additionalRequestParams={{
                            name: WhiteAndBlackListName.whiteListDsp,
                            type: WhiteAndBlackListType.White,
                            value: 'x',
                          }}
                        />

                        <WhiteOrBlackList
                          title="Add Countries to WL"
                          id={existingSsp.id}
                          primaryKeyName="ssp_id"
                          keyName="value"
                          postUrl={list.v2}
                          onDelete={onDelete(list.v2Delete)}
                          allowedValues={allowedWhiteListCountryValues}
                          hiddenColumns={['id']}
                          additionalRequestParams={{
                            name: WhiteAndBlackListName.whiteListSspCountries,
                            type: WhiteAndBlackListType.White,
                          }}
                        />
                      </>
                    ),
                  },
                ]
              : []),
            ...(existingSsp
              ? [
                  {
                    key: 'blackLists',
                    label: 'Black Lists',
                    children: (
                      <>
                        <WhiteOrBlackList
                          title="Add Crid to BL"
                          urlTemplate={list.v2}
                          primaryKeyName="ssp_id"
                          keyName="value"
                          postUrl={list.v2}
                          onEnd={onEnd}
                          onDelete={onDelete(list.v2Delete)}
                          hiddenColumns={['id']}
                          id={existingSsp.id}
                          additionalRequestParams={{
                            ssp_id: existingSsp.id,
                            name: WhiteAndBlackListName.blackListSspCrids,
                            type: WhiteAndBlackListType.Black,
                          }}
                        />

                        <WhiteOrBlackList
                          title="Add Adomain to BL"
                          urlTemplate={list.v2}
                          primaryKeyName="ssp_id"
                          keyName="value"
                          postUrl={list.v2}
                          onEnd={onEnd}
                          onDelete={onDelete(list.v2Delete)}
                          hiddenColumns={['id']}
                          id={existingSsp.id}
                          additionalRequestParams={{
                            ssp_id: existingSsp.id,
                            name: WhiteAndBlackListName.blackListSspAdomains,
                            type: WhiteAndBlackListType.Black,
                          }}
                        />
                      </>
                    ),
                  },
                ]
              : []),
            ...(existingSsp
              ? [
                  {
                    key: 'sellerInfo',
                    label: 'RTB Seller Info',
                    children: <SellerInfo sspId={existingSsp.id} />,
                  },
                ]
              : []),
          ]}
        />
      </Col>
    </Row>
  );
}
